/**
 * Generate Rows Button component for adding AI-generated rows to existing datasets.
 *
 * This component provides a button that allows users to generate additional rows
 * for an existing dataset using AI, maintaining schema consistency and context.
 */

import {Button} from '@wandb/weave/components/Button';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {
  prepareSingleShotMessages,
  useChatCompletionStream,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/magician';
import React, {useCallback, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {z} from 'zod';

import {useEntityProject} from '../context';
import {useCanAccessInference, useInferenceContext} from '../inference/util';
import {createDatasetRowsPrompt, DatasetRowSchema} from './llmMagics';

interface GenerateRowsButtonProps {
  /** Existing rows in the dataset to provide context */
  existingRows: any[];
  /** Callback function to add generated rows to the dataset */
  onRowsGenerated: (newRows: any[]) => void;
  /** Number of rows to generate (default: 5) */
  numRowsToGenerate?: number;
  /** Custom button text (optional) */
  buttonText?: string;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Lifted state: whether generation is in progress */
  isGenerating?: boolean;
  /** Lifted state setter: set generation state */
  setIsGenerating?: (isGenerating: boolean) => void;
  /** Lifted state: count of generated rows */
  generatedCount?: number;
  /** Lifted state setter: set generated count */
  setGeneratedCount?: (count: number) => void;
}

export const GenerateRowsButton: React.FC<GenerateRowsButtonProps> = ({
  existingRows,
  onRowsGenerated,
  numRowsToGenerate = 10,
  buttonText = 'Generate rows',
  disabled = false,
  variant = 'ghost',
  size = 'medium',
  isGenerating: isGeneratingProp,
  setIsGenerating: setIsGeneratingProp,
  generatedCount: generatedCountProp,
  setGeneratedCount: setGeneratedCountProp,
}) => {
  // Use lifted state if provided, otherwise fall back to local state
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
  const [generatedCountLocal, setGeneratedCountLocal] = useState(0);

  const isGenerating = isGeneratingProp ?? isGeneratingLocal;
  const setIsGenerating = setIsGeneratingProp ?? setIsGeneratingLocal;
  const generatedCount = generatedCountProp ?? generatedCountLocal;
  const setGeneratedCount = setGeneratedCountProp ?? setGeneratedCountLocal;

  const abortControllerRef = useRef<AbortController | null>(null);

  const {entity, project} = useEntityProject();
  const chatCompletionStream = useChatCompletionStream({entity, project});

  const inferenceContext = useInferenceContext();
  const {canAccessInference} = useCanAccessInference(inferenceContext);
  const hasInferenceAccess = canAccessInference ?? false;

  const handleGenerateRows = useCallback(async () => {
    if (!hasInferenceAccess) {
      toast.error('AI generation is not available for your account');
      return;
    }

    setIsGenerating(true);
    setGeneratedCount(0);

    const toastId = toast('Generating dataset rows...', {
      position: 'top-right',
      autoClose: false,
      closeOnClick: false,
      closeButton: true,
      type: 'info',
    });

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Filter out weave metadata from existing rows for context
      const cleanRows = existingRows.map(row => {
        const cleaned: any = {};
        Object.keys(row).forEach(key => {
          if (key !== '___weave') {
            cleaned[key] = row[key];
          }
        });
        return cleaned;
      });

      const messages = prepareSingleShotMessages({
        staticSystemPrompt: createDatasetRowsPrompt,
        generationSpecificContext: {
          existing_rows_sample: cleanRows.slice(0, 5), // Dataset row sample for context
        },
        additionalUserPrompt: `Generate ${numRowsToGenerate} new rows that fit with the existing dataset pattern and theme.`,
      });

      let previousRowCount = 0;

      await chatCompletionStream(
        {
          messages,
          temperature: 0.7,
          responseFormat: z.array(DatasetRowSchema),
          analyticsConfig: {
            task: 'generate_rows',
            area: 'dataset_editing',
          },
        },
        chunk => {
          try {
            const items = JSON.parse(chunk.content);
            if (Array.isArray(items) && items.length > previousRowCount) {
              // Extract only the new rows that haven't been processed yet
              const newRows = items.slice(previousRowCount);
              if (newRows.length > 0) {
                onRowsGenerated(newRows);
                previousRowCount = items.length;
                setGeneratedCount(items.length);
              }
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        controller.signal
      );

      // If streaming completed but we haven't reached the target count, still show success
      if (previousRowCount > 0) {
        toast.update(toastId, {
          render: 'Dataset rows generated successfully',
          type: 'success',
          autoClose: 3000,
          closeOnClick: true,
          closeButton: true,
        });
      } else {
        throw new Error('No valid data rows were generated. Please try again.');
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.message.includes('aborted')) {
        toast.update(toastId, {
          render: 'Row generation cancelled',
          type: 'info',
          autoClose: 3000,
          closeOnClick: true,
          closeButton: true,
        });
      } else {
        console.error('Error generating rows:', error);
        toast.update(toastId, {
          render: `Failed to generate rows: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          type: 'error',
          autoClose: 5000,
          closeOnClick: true,
          closeButton: true,
        });
      }
    } finally {
      setIsGenerating(false);
      setGeneratedCount(0);
      abortControllerRef.current = null;
    }
  }, [
    hasInferenceAccess,
    existingRows,
    numRowsToGenerate,
    chatCompletionStream,
    onRowsGenerated,
    setIsGenerating,
    setGeneratedCount,
  ]);

  // Don't render if user doesn't have inference access
  if (!hasInferenceAccess) {
    return null;
  }

  const buttonContent = isGenerating
    ? `Generating ${generatedCount} of ${numRowsToGenerate}...`
    : buttonText;

  return (
    <>
      {isGenerating ? (
        <Button
          onClick={handleGenerateRows}
          variant={variant}
          size={size}
          disabled={disabled || isGenerating}
          tooltip="Generating dataset rows">
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <WaveLoader size="small" />
            {buttonContent}
          </div>
        </Button>
      ) : (
        <Button
          icon="magic-wand-star"
          onClick={handleGenerateRows}
          variant={variant}
          size={size}
          disabled={disabled || isGenerating}
          tooltip={
            existingRows.length === 0
              ? `Generate ${numRowsToGenerate} rows using AI`
              : `Generate ${numRowsToGenerate} similar rows using AI`
          }>
          {buttonContent}
        </Button>
      )}
    </>
  );
};
